import React from 'react';

import { useIntl } from 'react-intl-next';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import {
	useEditorToolbar,
	AI_PASTE_MENU_SECTION,
	PASTE_MENU,
	PASTE_MENU_RANK,
} from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { PasteOptionsToolbarSharedState } from '@atlaskit/editor-plugin-paste-options-toolbar';
import {
	AICommandIcon,
	AISpellcheckIcon,
	ToolbarDropdownItem,
	ToolbarDropdownItemSection,
} from '@atlaskit/editor-toolbar';
import type { RegisterComponent } from '@atlaskit/editor-ui-control-model';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { expVal } from '@atlaskit/tmp-editor-statsig/expVal';

import type { AIPlugin } from '../../aiPluginType';
import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../prebuilt/config-items/config-items';
import {
	fixSpellingAndGrammar,
	improveWriting,
} from '../prebuilt/config-items/prompts/enhance/enhance';
import { addPolish } from '../prebuilt/config-items/prompts/polish/polish';

type AIPasteMenuItemProps = {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	configItem: EditorPluginAIConfigItem;
	icon: {
		(props: NewCoreIconProps): JSX.Element;
		displayName: string;
	};
	testId: string;
};

const AIPasteMenuItem = ({
	aiGlobalOptIn,
	api,
	configItem,
	icon,
	testId,
}: AIPasteMenuItemProps) => {
	const { editorView } = useEditorToolbar();
	const { formatMessage, locale } = useIntl();

	const { pasteStartPos, pasteEndPos } = useSharedPluginStateWithSelector(
		api,
		['pasteOptionsToolbarPlugin'],
		(states) => {
			const pluginState = states.pasteOptionsToolbarPluginState as
				| PasteOptionsToolbarSharedState
				| undefined;
			return {
				pasteStartPos: pluginState?.pasteStartPos ?? 0,
				pasteEndPos: pluginState?.pasteEndPos ?? 0,
			};
		},
	);

	const Icon = icon as React.ComponentType<NewCoreIconProps>;
	const label = formatMessage(configItem.title);

	return (
		<ToolbarDropdownItem
			testId={testId}
			elemBefore={<Icon size="small" label="" />}
			onClick={(event) => {
				if (!editorView) {
					return;
				}
				const isKeyboardTrigger = 'key' in event;
				const triggerMethod = isKeyboardTrigger ? 'keyboard' : 'click';
				const openAIModalCommand = createOpenAIModalCommand({
					state: editorView.state,
					configItem,
					lastTriggeredFrom: 'floatingToolbar',
					triggerMethod,
					aiGlobalOptIn,
					locale,
					overrideFromPos: pasteStartPos,
					overrideToPos: pasteEndPos,
				});
				openAIModalCommand(editorView.state, editorView.dispatch);
			}}
		>
			{label}
		</ToolbarDropdownItem>
	);
};

const AIPasteMenuSection = ({ children }: { children?: React.ReactNode }) => {
	return <ToolbarDropdownItemSection>{children}</ToolbarDropdownItemSection>;
};

export const AI_PASTE_MENU_KEY = 'ai-paste-menu';
const AI_PASTE_MENU_SECTION_KEY = AI_PASTE_MENU_SECTION.key;
const AI_PASTE_MENU_IMPROVE_WRITING_KEY = 'ai-paste-menu-improve-writing';
const AI_PASTE_MENU_FIX_SPELLING_KEY = 'ai-paste-menu-fix-spelling-and-grammar';
const AI_PASTE_MENU_ADD_POLISH_KEY = 'ai-paste-menu-add-polish';

type AIPasteMenuButtonConfig = {
	button: string;
	charMaxThreshold?: number;
	charMinThreshold: number;
	rank: number;
};

export const AI_PASTE_MENU_DISABLED_IN_NODES: string[] = ['codeBlock', 'heading'];

/**
 * Returns the active excluded nodes list, preferring the value from the `excludedNodes`
 * experiment param when it is non-empty and contains a valid string[]. Falls back to
 * `AI_PASTE_MENU_DISABLED_IN_NODES` otherwise.
 */
export const getActiveDisabledInNodes = (): string[] => {
	const raw = expVal('platform_editor_paste_actions_menu', 'excludedNodes', '');
	if (!raw) {
		return AI_PASTE_MENU_DISABLED_IN_NODES;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (Array.isArray(parsed) && parsed.length > 0 && parsed.every((n) => typeof n === 'string')) {
			return parsed as string[];
		}
	} catch {
		// Invalid JSON – fall through to default.
	}
	return AI_PASTE_MENU_DISABLED_IN_NODES;
};

export const AI_PASTE_MENU_CONFIG: AIPasteMenuButtonConfig[] = [
	{
		button: AI_PASTE_MENU_IMPROVE_WRITING_KEY,
		charMinThreshold: 500,
		rank: 1,
	},
	{
		button: AI_PASTE_MENU_ADD_POLISH_KEY,
		charMinThreshold: 3000,
		rank: 2,
	},
];

const isAIPasteMenuButtonConfig = (value: unknown): value is AIPasteMenuButtonConfig =>
	typeof value === 'object' &&
	value !== null &&
	typeof (value as AIPasteMenuButtonConfig).button === 'string' &&
	typeof (value as AIPasteMenuButtonConfig).charMinThreshold === 'number' &&
	(typeof (value as AIPasteMenuButtonConfig).charMaxThreshold === 'number' ||
		(value as AIPasteMenuButtonConfig).charMaxThreshold === undefined) &&
	typeof (value as AIPasteMenuButtonConfig).rank === 'number';

/**
 * Returns the active menu config, preferring the value from the `menuConfig` experiment
 * param when it is non-empty and contains a valid `AIPasteMenuButtonConfig[]`. Falls back
 * to the hardcoded `AI_PASTE_MENU_CONFIG` otherwise.
 */
export const getActiveMenuConfig = (): AIPasteMenuButtonConfig[] => {
	const raw = expVal('platform_editor_paste_actions_menu', 'menuConfig', '');
	if (!raw) {
		return AI_PASTE_MENU_CONFIG;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(isAIPasteMenuButtonConfig)) {
			return parsed;
		}
	} catch {
		// Invalid JSON – fall through to default.
	}
	return AI_PASTE_MENU_CONFIG;
};

export const getAIPasteMenuComponents = ({
	aiGlobalOptIn,
	api,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
}): RegisterComponent[] => {
	const activeConfig = getActiveMenuConfig();

	const getPlaintextLength = () => {
		const state = api?.pasteOptionsToolbarPlugin?.sharedState.currentState() as
			| PasteOptionsToolbarSharedState
			| undefined;
		return state?.plaintextLength ?? 0;
	};

	const getAncestorNodeNames = (): string[] => {
		const state = api?.pasteOptionsToolbarPlugin?.sharedState.currentState() as
			| PasteOptionsToolbarSharedState
			| undefined;
		return state?.pasteAncestorNodeNames ?? [];
	};

	const configByKey = Object.fromEntries(activeConfig.map((c) => [c.button, c]));

	const makeIsHidden = (key: string) => () => {
		const config = configByKey[key];
		if (!config) {
			return true;
		}
		const length = getPlaintextLength();
		const ancestorNodeNames = getAncestorNodeNames();
		const belowMin = length < config.charMinThreshold;
		const aboveMax = config.charMaxThreshold !== undefined && length > config.charMaxThreshold;
		const inDisabledNode = ancestorNodeNames.some((name) =>
			getActiveDisabledInNodes().includes(name),
		);
		return belowMin || aboveMax || inDisabledNode;
	};

	return [
		{
			type: 'menu-section',
			key: AI_PASTE_MENU_SECTION_KEY,
			parents: [
				{
					type: PASTE_MENU.type,
					key: PASTE_MENU.key,
					rank: PASTE_MENU_RANK[AI_PASTE_MENU_SECTION_KEY],
				},
			],
			component: AIPasteMenuSection,
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_IMPROVE_WRITING_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: configByKey[AI_PASTE_MENU_IMPROVE_WRITING_KEY]?.rank ?? 0,
				},
			],
			isHidden: makeIsHidden(AI_PASTE_MENU_IMPROVE_WRITING_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={improveWriting()}
					icon={AICommandIcon}
					testId="ai-paste-menu__improve-writing"
				/>
			),
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_FIX_SPELLING_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: configByKey[AI_PASTE_MENU_FIX_SPELLING_KEY]?.rank ?? 0,
				},
			],
			isHidden: makeIsHidden(AI_PASTE_MENU_FIX_SPELLING_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={fixSpellingAndGrammar()}
					icon={AISpellcheckIcon}
					testId="ai-paste-menu__fix-spelling-and-grammar"
				/>
			),
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_ADD_POLISH_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: configByKey[AI_PASTE_MENU_ADD_POLISH_KEY]?.rank ?? 0,
				},
			],
			isHidden: makeIsHidden(AI_PASTE_MENU_ADD_POLISH_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={addPolish()}
					icon={AiGenerativeCleanupIcon}
					testId="ai-paste-menu__add-polish"
				/>
			),
		},
	];
};
